/**
 * Pure helpers for the bulk resolve dialog's previews: the "Will update"
 * column, the Preview table's per-source rows, and each option's "Applies to
 * X of N" caption.
 */
import {
  ALL_BULK_TARGETS,
  ALREADY_DECIDED_REASON,
  computeBulkPlan,
  isBulkSource,
  type BulkAnswer,
  type BulkPlan,
  type BulkResolveRow,
  type BulkRowOutcome,
  type BulkSource,
} from "./bulkResolveWithValue.helpers";
import { computeActionEligibility, getFieldLabel } from "./DiscrepancyReviewDialog.helpers";
import { type ResolveActionOption } from "./ResolveDiscrepanciesDialog";
import {
  computeDocumentEligibility,
  computeVendorEligibility,
  type ResolutionTargetLiteral,
} from "./resolveWithValue.helpers";
import { type CopyKey } from "./resolveWithValue.outcome";

/** The copy each source answer reads from, so it is never a write target. */
const SOURCE_COPY: Record<BulkSource, ResolutionTargetLiteral> = {
  EMANIFEST: "EMANIFEST",
  VENDOR: "VENDOR_DATA",
  DOCUMENT: "DOCUMENT",
};

/** Short names used in the "Will update" column. */
const WILL_UPDATE_LABELS: Record<ResolutionTargetLiteral, string> = {
  VENDOR_DATA: "Vendor",
  EMANIFEST: "e-Manifest (needs correction)",
  DOCUMENT: "Document",
};

/** One line of what an answer does beyond its label; null when the label says it all. */
export const BULK_OPTION_DESCRIPTIONS: Record<BulkAnswer, string | null> = {
  EMANIFEST: null,
  VENDOR:
    "Rows where e-Manifest differs are marked Needs correction in e-Manifest until it's corrected in RCRAInfo.",
  DOCUMENT:
    "Rows where e-Manifest differs are marked Needs correction in e-Manifest until it's corrected in RCRAInfo.",
  DISMISS: "It reopens if a value changes on a later sync.",
  CANT_DETERMINE:
    "The manifest leaves the actionable queue until someone unblocks it. Nothing is resolved.",
};

export interface CopyTargetState {
  target: ResolutionTargetLiteral;
  /** Why this copy is not written (it is the picked value, or no row has a document value), or null when it is. */
  disabledReason: string | null;
}

/** Every copy, in display order. The source's own copy and (with no document on any row) the document copy are disabled with a reason. */
export function copyTargetStates(
  source: BulkSource,
  rows: readonly BulkResolveRow[]
): CopyTargetState[] {
  const hasDocument = rows.some((r) => r.manifestDocumentValue != null);
  return ALL_BULK_TARGETS.map((target) => {
    if (target === SOURCE_COPY[source])
      return { target, disabledReason: "This is the value you picked" };
    if (target === "DOCUMENT" && !hasDocument) {
      return { target, disabledReason: "No selected row has a document value" };
    }
    return { target, disabledReason: null };
  });
}

export interface WillUpdateCell {
  text: string;
  muted: boolean;
}

const PENDING_CELL: WillUpdateCell = { text: "—", muted: true };

/** The source hierarchy: Document, e-Manifest, Vendor. */
const WILL_UPDATE_ORDER: readonly ResolutionTargetLiteral[] = [
  "DOCUMENT",
  "EMANIFEST",
  "VENDOR_DATA",
];

function valueCell(
  id: string,
  plan: BulkPlan,
  outcomes: ReadonlyMap<string, BulkRowOutcome>
): WillUpdateCell {
  const row = plan.updatable.find((r) => r.id === id);
  if (row) {
    const text = WILL_UPDATE_ORDER.filter((t) => row.applyTo.includes(t))
      .map((t) => WILL_UPDATE_LABELS[t])
      .join(" · ");
    const staysOpen = outcomes.get(id)?.outcome === "STAYS_OPEN";
    return { text: staysOpen ? `${text} (stays Pending Review)` : text, muted: false };
  }
  const skipped = plan.skippedRows.find((r) => r.id === id);
  return { text: `Skipped: ${skipped?.reason ?? "not eligible"}`, muted: true };
}

function optionCell(
  id: string,
  option: ResolveActionOption | undefined,
  text: string,
  skipReason?: string
) {
  if (option?.eligibleIds.includes(id)) return { text, muted: false };
  return { text: skipReason ? `Skipped: ${skipReason}` : "Skipped", muted: true };
}

/** Why the dismiss option can't take each row, from the same eligibility rule the parent used. */
export function dismissSkipReasonsById(
  rows: readonly BulkResolveRow[],
  dismissOption: ResolveActionOption
): Map<string, string> {
  const reasons = new Map<string, string>();
  for (const row of rows) {
    if (dismissOption.eligibleIds.includes(row.id)) continue;
    const [skip] = computeActionEligibility("nonDiscrepancy", [row]).skips;
    if (skip) reasons.set(row.id, skip.reason);
  }
  return reasons;
}

/** What Save writes for one row under the current answer and plan; "—" before an answer is picked. */
export function willUpdateCell(args: {
  id: string;
  /** The row's status; a row that is not Pending Review never takes a value answer. */
  status?: string;
  answer: BulkAnswer | null;
  plan: BulkPlan | null;
  outcomes?: ReadonlyMap<string, BulkRowOutcome>;
  dismissOption: ResolveActionOption;
  /** Why each row the dismiss option can't take is skipped, by id. */
  dismissSkipReasons?: ReadonlyMap<string, string>;
  cantDetermineOption?: ResolveActionOption;
}): WillUpdateCell {
  const { id, status, answer, plan, dismissOption, cantDetermineOption } = args;
  const decided = status != null && status !== "UNRESOLVED";
  if (decided && (!answer || isBulkSource(answer))) {
    return { text: `Skipped: ${ALREADY_DECIDED_REASON}`, muted: true };
  }
  if (!answer) return PENDING_CELL;
  if (answer === "DISMISS") {
    return optionCell(id, dismissOption, "Non-Discrepancy", args.dismissSkipReasons?.get(id));
  }
  if (answer === "CANT_DETERMINE") return optionCell(id, cantDetermineOption, "Blocks manifest");
  return plan && isBulkSource(answer)
    ? valueCell(id, plan, args.outcomes ?? new Map())
    : PENDING_CELL;
}

export interface OptionSummary {
  applies: number;
  total: number;
  /** True when the option applies to at least one row. */
  selectable: boolean;
}

function summarizeOption(total: number, option: ResolveActionOption | undefined): OptionSummary {
  const applies = option?.eligibleIds.length ?? 0;
  return {
    applies,
    total,
    selectable: applies > 0,
  };
}

function summarizeSource(rows: readonly BulkResolveRow[], source: BulkSource): OptionSummary {
  const plan = computeBulkPlan(rows, source);
  return {
    applies: plan.updatable.length,
    total: rows.length,
    selectable: plan.updatable.length > 0,
  };
}

/** Every option's counts; value answers use the plan that writes every source that differs. */
export function computeOptionSummaries(
  rows: readonly BulkResolveRow[],
  dismissOption: ResolveActionOption,
  cantDetermineOption?: ResolveActionOption
): Record<BulkAnswer, OptionSummary> {
  return {
    EMANIFEST: summarizeSource(rows, "EMANIFEST"),
    VENDOR: summarizeSource(rows, "VENDOR"),
    DOCUMENT: summarizeSource(rows, "DOCUMENT"),
    DISMISS: summarizeOption(rows.length, dismissOption),
    CANT_DETERMINE: summarizeOption(rows.length, cantDetermineOption),
  };
}

/** Skip reasons stay in the table's Will update column, which names each row. */
export function formatOptionCaption(summary: OptionSummary): string {
  return `Applies to ${summary.applies} of ${summary.total}`;
}

const WARNING_COPY_NAMES: Record<CopyKey, string> = {
  vendor: "vendor data",
  eManifest: "e-Manifest value",
  document: "manifest document",
};

const COPY_ORDER: readonly CopyKey[] = ["vendor", "eManifest", "document"];

/** The field label when this copy can't take the write for this row, else null. */
function blockedFieldFor(row: BulkResolveRow, source: BulkSource, copy: CopyKey): string | null {
  if (copy === "eManifest") return null;
  const eligible =
    copy === "vendor"
      ? computeVendorEligibility([row], source === "DOCUMENT", "document's value").eligibleIds
      : computeDocumentEligibility([row]).eligibleIds;
  return eligible.length > 0 ? null : getFieldLabel(row.field);
}

function rowsText(count: number, singular: string, plural: string): string {
  return count === 1 ? `1 row ${singular}` : `${count} rows ${plural}`;
}

function possessive(count: number): string {
  return count === 1 ? "its" : "their";
}

interface WarningBucket {
  copy: CopyKey;
  blockedField: string | null;
  count: number;
}

function addToBucket(
  buckets: Map<string, WarningBucket>,
  copy: CopyKey,
  blockedField: string | null
) {
  const key = `${copy}|${blockedField ?? ""}`;
  const bucket = buckets.get(key) ?? { copy, blockedField, count: 0 };
  bucket.count += 1;
  buckets.set(key, bucket);
}

function sortedBuckets(buckets: Map<string, WarningBucket>): WarningBucket[] {
  return [...buckets.values()].sort(
    (a, b) => COPY_ORDER.indexOf(a.copy) - COPY_ORDER.indexOf(b.copy)
  );
}

function stayOpenLine({ copy, blockedField, count }: WarningBucket): string {
  const head = `${rowsText(count, "stays", "stay")} Pending Review because ${possessive(count)} ${WARNING_COPY_NAMES[copy]} still disagrees`;
  return blockedField ? `${head}, and ${blockedField} can't be corrected there.` : `${head}.`;
}

function fixPendingLine({ copy, count }: WarningBucket): string {
  const goes = rowsText(count, "goes", "go");
  const they = count === 1 ? "It goes" : "They go";
  return `${goes} to Needs correction in e-Manifest, but ${possessive(count)} ${WARNING_COPY_NAMES[copy]} still disagrees. ${they} back to Pending Review once RCRAInfo is corrected.`;
}

/**
 * Lines naming the copies that still disagree after the writes: rows that
 * stay Pending Review, and Needs correction in e-Manifest rows that go back to Pending Review
 * once RCRAInfo is corrected.
 */
export function unmatchedCopyWarnings(
  rows: readonly BulkResolveRow[],
  source: BulkSource,
  outcomes: ReadonlyMap<string, BulkRowOutcome>
): string[] {
  const rowsById = new Map(rows.map((r) => [r.id, r]));
  const stayOpen = new Map<string, WarningBucket>();
  const fixPending = new Map<string, WarningBucket>();
  for (const [id, { outcome, left }] of outcomes) {
    const row = rowsById.get(id);
    if (!row) continue;
    for (const copy of left) {
      if (outcome === "STAYS_OPEN") addToBucket(stayOpen, copy, blockedFieldFor(row, source, copy));
      else if (outcome === "PENDING") addToBucket(fixPending, copy, null);
    }
  }
  return [
    ...sortedBuckets(stayOpen).map(stayOpenLine),
    ...sortedBuckets(fixPending).map(fixPendingLine),
  ];
}
